/**
 * Portal v2 login page (`/login`).
 *
 * Domain-based login flow for MSP child accounts:
 *   step 1: domain → POST /msp/domain-login (backend looks up admin email)
 *   step 2: success → user receives OTP+magic link by email; CTA navigates
 *           to /verify-otp where they enter the 6-digit code, OR they can
 *           click the magic link in their inbox (lands on /verify-domain).
 *
 * Branded via BrandingProvider (logo + primary color flow through the token
 * cascade). No password login (OTP-only by design).
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  Globe,
  Loader2,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { Button, Input, Label } from "@ddmarc/ui";
import { useBranding } from "@/lib/branding-context";
import { CardV2 } from "@/components/primitives";
import { PortalBrand } from "@/components/portal-brand";

// Allowable domain shape (a label + dot + TLD); matches partner-side regex.
const DOMAIN_RE = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;

interface DomainLoginResponse {
  success: boolean;
  message?: string;
  expires_in?: number;
}

export function PortalLoginV2Page() {
  const { branding, isLoading: brandingLoading } = useBranding();
  const navigate = useNavigate();
  const [domain, setDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    domain: string;
    expiresIn: number | null;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!DOMAIN_RE.test(domain)) {
      setError("Please enter a valid domain (e.g. example.com)");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/msp/domain-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Send the browser's current host so the backend can scope login to
        // the MSP that owns this vanity host (its customers only). OTP stays
        // the auth boundary — the host just narrows which tenant we resolve.
        body: JSON.stringify({ domain, portal_host: window.location.hostname }),
        credentials: "include",
      });
      const data: DomainLoginResponse & { detail?: string } = await response
        .json()
        .catch(() => ({ success: false }));
      if (!response.ok || !data.success) {
        throw new Error(
          data.detail || data.message || "Unable to send login link"
        );
      }
      setSuccess({
        domain,
        expiresIn: data.expires_in ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const goToOtpPage = () => {
    if (!success) return;
    navigate({
      to: "/verify-otp",
      search: {
        domain: success.domain,
        expires_in: success.expiresIn || undefined,
      },
    });
  };

  const handleTryAgain = () => {
    setSuccess(null);
    setDomain("");
    setError(null);
  };

  if (brandingLoading) {
    return (
      <div
        data-ui="v2"
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div
      data-ui="v2"
      className="flex min-h-screen flex-col items-center justify-center bg-background p-4"
    >
      <div className="mb-6 flex flex-col items-center gap-3">
        <PortalBrand branding={branding} size="lg" />
      </div>

      <CardV2
        className="w-full max-w-md"
        title={
          <span className="text-center text-[16px] font-semibold">
            {success ? "Check your email" : "Sign in to your account"}
          </span>
        }
        subtitle={
          <span className="block text-center">
            {success
              ? "If this domain is registered for the portal, a verification code and login link are on their way to its administrator."
              : "Enter your domain to receive a login link via email."}
          </span>
        }
      >
        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div className="space-y-1.5">
              <Label htmlFor="domain">Your domain</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="domain"
                  type="text"
                  placeholder="example.com"
                  autoComplete="off"
                  autoCapitalize="off"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {error && (
              <p className="text-[12.5px] text-[var(--v2-danger)]" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="mr-2 h-4 w-4" />
              )}
              Send login link
            </Button>
          </form>
        ) : (
          <div className="space-y-3.5">
            <Button onClick={goToOtpPage} className="w-full">
              Enter verification code
            </Button>

            <div className="rounded-md border border-border bg-card p-3 text-[12px] text-muted-foreground">
              <div className="flex items-start gap-2">
                <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>
                  Or click the magic link in your email to sign in
                  automatically. Check your spam folder if you don't see it.
                </p>
              </div>
            </div>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleTryAgain}
            >
              Try a different domain
            </Button>
          </div>
        )}

        <p className="mt-4 flex items-center justify-center gap-1.5 text-[11.5px] text-[var(--v2-fg-faint)]">
          <ShieldCheck className="h-3 w-3" />
          Secured with one-time codes
        </p>
      </CardV2>

      {(branding?.support_url || branding?.support_email) && (
        <p className="mt-6 text-[12.5px] text-muted-foreground">
          Need help?{" "}
          <a
            // Prefer the MSP's support page; fall back to a mailto only when no
            // support URL is configured. External page opens in a new tab.
            href={branding?.support_url ?? `mailto:${branding?.support_email}`}
            {...(branding?.support_url
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className="font-medium text-[var(--color-primary)] hover:underline"
          >
            Contact support
          </a>
        </p>
      )}

      <span hidden>
        <AlertTriangle aria-hidden />
      </span>
    </div>
  );
}
